import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { CombinedAnalysisResponse, ContaminantInput } from '../../api/polygon';

// Tab types for the dashboard
type TabId = 'plume-2d' | 'plume-3d' | 'charts' | 'cost-analysis' | 'site-data' | 'wells' | 'predictions' | 'delineation-intelligence';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Error boundary for map component
class MapErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean; errorMessage: string }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Map error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="h-[500px] bg-mono-950 rounded-lg flex items-center justify-center border border-mono-800">
          <div className="text-center p-4">
            <p className="text-red-400 text-sm font-semibold mb-2">MAP FAILED TO LOAD</p>
            <p className="text-mono-500 text-xs">{this.state.errorMessage}</p>
            <p className="text-mono-600 text-xs mt-2">Check browser console for details</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  BeakerIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowPathIcon,
  TrashIcon,
  FolderIcon,
  MapIcon,
  TableCellsIcon,
  Cog6ToothIcon,
  CircleStackIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { FadeIn, StaggeredFadeIn } from '../../components/animations/FadeIn';
// PlumeMapSatellite removed - using ThreePlumeVisualization as main display
import { ThreePlumeVisualization } from '../../components/polygon/ThreePlumeVisualization';
import { ThreePlumeChart } from '../../components/polygon/ThreePlumeChart';
import { AnalyteHistoryChart } from '../../components/polygon/AnalyteHistoryChart';
import { DelineationIntelligencePanel } from '../../components/polygon/DelineationIntelligencePanel';
import { RegLibrarySelector } from '../../components/polygon/RegLibrarySelector';
import { ContaminantStatsColumn } from '../../components/polygon/ContaminantStatsColumn';
import { GradientDataTable } from '../../components/polygon/GradientDataTable';
import { WellManagementTable } from '../../components/polygon/WellManagementTable';
import { CostAnalysisTab } from '../../components/polygon/CostAnalysisTab';
import { SiteDataTab } from '../../components/polygon/SiteDataTab';
import { Sparkline } from '../data-vault/components/ui/Sparkline';
import { HorizonPredictionsTable } from '../../components/polygon/HorizonPredictionsTable';
import { polygonApi, SiteDetailsResponse, PeriodPrediction, ExistingWell, PlumeStateData, ThreePlumeResponse, RecoveryWell, MDPEWell, ThreePlumeRequest, SiteGradientMap, ExtractedDataResponse, ExtractedWell, ExtractedGauging, ExtractedAnalyticalResult, TWDBQueryResponse, TWDBWell, WellPredictionsResponse, WellPrediction, CostProjectionSnapshotResponse, fetchCostProjectionSnapshot, PageBundleResponse, TimelineResponse, PlumeSnapshotsResponse, ContaminantBoundaryData, TimelinePredictionsResponse, ObservedMetricsResponse, fetchV32CostCurve, type V32CostCurveResponse } from '../../api/polygon';

// Internal site data structure after transformation
interface SiteData {
  site_id: string;
  site_name: string;
  analysis_timestamp: string;
  latitude: number;
  longitude: number;
  threat_tier: 1 | 2 | 3 | 4 | 5;
  risk_summary: string;
  predictions: PeriodPrediction[];
  plume_states: PlumeStateData[];
  // PSH/LNAPL source zone parameters (from database or extraction)
  psh_thickness_ft: number | null;
  psh_area_sqft: number | null;
  estimated_mass_lbs: number | null;
  plume_area_sqft: number;
  max_concentration_ug_L: number;
  contaminant_type: string;
  toolkit_data: {
    // Soil properties (SSURGO)
    soil_type: string;
    aquifer_k_ft_day: number;
    aquifer_porosity: number;
    depth_to_water_ft: number;
    land_use: string;
    clay_pct: number;
    sand_pct: number;
    organic_matter_pct: number;
    organic_carbon_fraction: number;
    ksat_in_hr: number;
    hydrologic_group: string;
    // Groundwater (NWIS)
    water_level_date: string;
    water_elevation_ft: number;
    // Elevation/gradient (3DEP)
    ground_elevation_ft: number;
    hydraulic_gradient: number;
    // Aquifer info
    aquifer_type: string;
    aquifer_name: string;
    aquifer_code: string;
    // Calculated
    groundwater_velocity_ft_day: number;
    // Data quality
    data_sources: string[];
    confidence_score: number;
  };
  physics: {
    darcy_velocity_ft_day: number;
    seepage_velocity_ft_day: number;
    annual_migration_ft: number;
    flow_direction_degrees: number;
  } | null;
  existing_wells: ExistingWell[];
  recommended_wells: RecommendedWell[];
  total_projection_months: number;
  total_estimated_cost: number;
  total_wells_to_install: number;
}

interface RecommendedWell {
  well_id: string;
  latitude: number;
  longitude: number;
  well_type: string;
  purpose: string;
  recommended_depth_ft: number;
  priority: string;
  install_by_month: number;
  estimated_cost: number;
  distance_from_source_ft: number;
}

// Navigation state passed from PolygonUpload
interface NavigationState {
  latitude: number;
  longitude: number;
  plume_area_sqft: number;
  max_concentration_ug_L: number;
  contaminant_type?: string;
  site_name?: string;
  contaminants?: ContaminantInput[];
  combinedAnalysis?: CombinedAnalysisResponse;
  threePlumeResult?: unknown;
  psh_present?: boolean;
  remediation_type?: string;
  // PSH/LNAPL source zone parameters (from database or document extraction)
  psh_thickness_ft?: number | null;
  psh_area_sqft?: number | null;
  estimated_mass_lbs?: number | null;
  // Extracted gradient and plume data for visualization
  extractedGradientData?: {
    contours?: { elevation_ft: number; coordinates: [number, number][] }[];
    flow_arrows?: { lat: number; lon: number; direction_deg: number; magnitude?: number }[];
    well_readings?: { well_id: string; lat: number; lon: number; wt_elev_ft: number }[];
    average_gradient?: number;
    average_flow_direction_deg?: number;
  } | null;
  extractedPlumeBoundaries?: { concentration_ug_l: number; boundary_coords: [number, number][] }[] | null;
  extractedSourceLocation?: { lat: number; lon: number } | null;
  // Extracted well inventory for auto-populating recovery/MDPE wells
  extractedWellInventory?: {
    well_id: string;
    // 'monitor' is from API, 'monitoring' is frontend standard - both are accepted
    well_type: 'monitoring' | 'monitor' | 'recovery' | 'mdpe' | 'sve' | 'air_sparge' | 'injection' | 'other' | string;
    lat: number;
    lon: number;
    status?: string;
    depth_ft?: number;
    pump_rate_gpm?: number;
  }[] | null;
}

// Transform navigation state (CombinedAnalysisResponse) to component data structure
// Note: Backend uses different field names than frontend interface:
// - hydrogeology (not hydrogeological_data)
// - physics_simulation (not physics_results)
// - physics_simulation.plume_timeline (with 'month' field)
// - wells.existing_wells (nested under wells object)
function transformNavigationState(navState: NavigationState): SiteData {
  const combined = navState.combinedAnalysis as Record<string, unknown> | undefined;

  // Handle both field naming conventions (backend vs frontend interface)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const physicsResults = (combined?.physics_results || combined?.physics_simulation) as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hydroData = (combined?.hydrogeological_data || combined?.hydrogeology) as any;

  // Get plume states from physics results
  // Backend sends plume_timeline with 'month' field, frontend uses plume_states with 'time_months'
  let plumeStates: PlumeStateData[] = [];
  if (physicsResults?.plume_states) {
    plumeStates = physicsResults.plume_states;
  } else if (physicsResults?.plume_timeline) {
    // Transform plume_timeline format to plume_states format
    plumeStates = physicsResults.plume_timeline.map((item: Record<string, unknown>) => ({
      time_months: item.month || item.time_months,
      plume_area_sqft: item.plume_area_sqft || item.area_sqft,
      plume_length_ft: item.plume_length_ft || item.length_ft,
      plume_width_ft: item.plume_width_ft || item.width_ft,
      max_concentration_ug_L: item.max_concentration_ug_L || item.max_conc_ug_L,
      migration_distance_ft: item.migration_distance_ft || item.migration_ft,
      centroid_lat: item.centroid_lat,
      centroid_lon: item.centroid_lon,
      contours: item.contours || [],
    }));
  }

  // Default hydrogeology if not available
  const hydro = hydroData || {
    hydraulic_conductivity_ft_day: 10,
    porosity: 0.35,
    hydraulic_gradient: 0.005,
    flow_direction_degrees: 180,
    aquifer_name: 'Unknown',
    depth_to_water_ft: 50,
  };

  // Threat tier + cost predictions now come from pre-computed backend snapshots.
  // Default tier (3 = MODERATE) used only until snapshot loads.
  const conc = navState.max_concentration_ug_L ||
    (combined?.contaminant as Record<string, unknown>)?.initial_concentration_ug_L as number || 0;
  const threatTier: 1 | 2 | 3 | 4 | 5 = 3;
  const predictions: PeriodPrediction[] = [];

  // Calculate velocities
  const K = hydro.hydraulic_conductivity_ft_day || 10;
  const n = hydro.porosity || 0.35;
  const i = hydro.hydraulic_gradient || 0.005;
  const darcyVelocity = K * i;
  const seepageVelocity = darcyVelocity / n;
  const annualMigration = seepageVelocity * 365;

  // Get wells - handle multiple possible response structures
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wellsData = combined?.wells as any;

  // Try multiple paths to find existing wells
  let rawExistingWells: unknown[] = [];

  // Path 1: combined.existing_wells (direct)
  if (Array.isArray(combined?.existing_wells) && (combined?.existing_wells as unknown[]).length > 0) {
    rawExistingWells = combined.existing_wells as unknown[];
  }
  // Path 2: combined.wells.existing_wells (nested)
  else if (Array.isArray(wellsData?.existing_wells) && wellsData.existing_wells.length > 0) {
    rawExistingWells = wellsData.existing_wells;
  }
  // Path 3: combined.wells is an array directly
  else if (Array.isArray(wellsData) && wellsData.length > 0) {
    rawExistingWells = wellsData;
  }
  // Path 4: Check for any key containing 'well' in the response
  else if (combined) {
    for (const key of Object.keys(combined)) {
      const value = (combined as Record<string, unknown>)[key];
      if (key.toLowerCase().includes('well') && Array.isArray(value) && value.length > 0) {
        rawExistingWells = value;
        break;
      }
    }
  }

  // Get recommended wells from progressive well plan
  const rawRecommendedWells = combined?.recommended_wells || wellsData?.recommended_wells ||
    wellsData?.progressive_well_plan?.period_recommendations?.flatMap(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rec: any) => rec.new_wells || rec.new_wells_recommended || []
    ) || [];

  // Transform wells - handle multiple possible field names
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const existingWells: ExistingWell[] = (rawExistingWells as any[]).map(w => {
    // Handle different possible field names for lat/lon
    const lat = w.latitude ?? w.lat ?? w.Latitude ?? w.LAT;
    const lon = w.longitude ?? w.lon ?? w.lng ?? w.Longitude ?? w.LON ?? w.LNG;

    return {
      well_id: w.well_id || w.wellId || w.id || `WELL-${Math.random().toString(36).substr(2, 6)}`,
      latitude: lat,
      longitude: lon,
      source_database: w.source_database || w.source || w.sourceDatabase || 'Unknown',
      source: w.source_database || w.source || w.sourceDatabase || 'Unknown',
      well_depth_ft: w.well_depth_ft || w.wellDepth || w.depth_ft || w.depth,
      distance_ft: w.distance_ft || w.distanceFt || w.distance,
      well_type: w.well_type || w.wellType || 'monitor',
      status: w.status || 'active',
    };
  }).filter(w => typeof w.latitude === 'number' && typeof w.longitude === 'number' && !isNaN(w.latitude) && !isNaN(w.longitude));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recommendedWells: RecommendedWell[] = (rawRecommendedWells as any[]).map(w => ({
    well_id: w.well_id,
    latitude: w.latitude,
    longitude: w.longitude,
    well_type: w.well_type || 'monitoring',
    purpose: w.purpose,
    recommended_depth_ft: w.recommended_depth_ft,
    priority: w.priority || 'medium',
    install_by_month: w.install_by_month || 6,
    estimated_cost: w.estimated_install_cost || w.cost || 15000,
    distance_from_source_ft: w.distance_from_source_ft || 0,
  }));

  // Get site location
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const siteLocation = combined?.site_location as any;

  // Get PSH data from navState or combinedAnalysis
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pshData = combined?.psh_data as any;
  const pshThickness = navState.psh_thickness_ft ?? pshData?.thickness_ft ?? (combined?.psh_thickness_ft as number | null) ?? null;
  const pshArea = navState.psh_area_sqft ?? pshData?.area_sqft ?? (combined?.psh_area_sqft as number | null) ?? null;
  const estimatedMass = navState.estimated_mass_lbs ?? pshData?.estimated_mass_lbs ?? (combined?.estimated_mass_lbs as number | null) ?? null;

  return {
    site_id: (combined?.analysis_id || combined?.site_id || `SITE-${Date.now()}`) as string,
    site_name: navState.site_name || `Site ${combined?.analysis_id || 'Unknown'}`,
    analysis_timestamp: (combined?.analysis_timestamp || combined?.analysis_date || new Date().toISOString()) as string,
    latitude: navState.latitude || siteLocation?.latitude || 0,
    longitude: navState.longitude || siteLocation?.longitude || 0,
    threat_tier: threatTier,
    risk_summary: threatTier <= 2
      ? `High concentration (${conc.toFixed(0)} ug/L) requires immediate attention. Plume area of ${((navState.plume_area_sqft || 0) / 1000).toFixed(1)}K sqft at risk of expansion.`
      : `Site monitoring recommended. Current concentration at ${conc.toFixed(0)} ug/L.`,
    predictions,
    plume_states: plumeStates,
    // PSH/LNAPL source zone parameters
    psh_thickness_ft: pshThickness,
    psh_area_sqft: pshArea,
    estimated_mass_lbs: estimatedMass,
    plume_area_sqft: navState.plume_area_sqft || 0,
    max_concentration_ug_L: conc,
    contaminant_type: navState.contaminant_type || 'benzene',
    toolkit_data: {
      // Soil properties (SSURGO)
      soil_type: hydro.soil_type || 'Sandy Loam',
      aquifer_k_ft_day: K,
      aquifer_porosity: n,
      depth_to_water_ft: hydro.depth_to_water_ft || 50,
      land_use: hydro.land_use || 'Industrial',
      clay_pct: hydro.clay_pct || 0,
      sand_pct: hydro.sand_pct || 0,
      organic_matter_pct: hydro.organic_matter_pct || 0,
      organic_carbon_fraction: hydro.organic_carbon_fraction || 0,
      ksat_in_hr: hydro.ksat_in_hr || 0,
      hydrologic_group: hydro.hydrologic_group || 'B',
      // Groundwater (NWIS)
      water_level_date: hydro.water_level_date || '',
      water_elevation_ft: hydro.water_elevation_ft || 0,
      // Elevation/gradient (3DEP)
      ground_elevation_ft: hydro.ground_elevation_ft || 0,
      hydraulic_gradient: i,
      // Aquifer info
      aquifer_type: hydro.aquifer_type || 'unconfined',
      aquifer_name: hydro.aquifer_name || 'Unknown',
      aquifer_code: hydro.aquifer_code || '',
      // Calculated
      groundwater_velocity_ft_day: seepageVelocity,
      // Data quality
      data_sources: hydro.data_sources || [],
      confidence_score: hydro.confidence_score || 0,
    },
    physics: {
      darcy_velocity_ft_day: darcyVelocity,
      seepage_velocity_ft_day: seepageVelocity,
      annual_migration_ft: annualMigration,
      flow_direction_degrees: hydro.flow_direction_degrees || 180,
    },
    existing_wells: existingWells,
    recommended_wells: recommendedWells,
    total_projection_months: plumeStates.length > 0 ? plumeStates[plumeStates.length - 1].time_months : 24,
    total_estimated_cost: 0,  // From pre-computed cost snapshot
    total_wells_to_install: 0,  // From pre-computed cost snapshot
  };
}

// Transform API response to component data structure
function transformSiteDetails(details: SiteDetailsResponse): SiteData {
  const plumeStates = details.simulation?.plume_states || [];
  const hydro = details.hydrogeology || {
    hydraulic_conductivity_ft_day: 10,
    porosity: 0.35,
    hydraulic_gradient: 0.005,
    flow_direction_degrees: 180,
    aquifer_name: 'Unknown',
  };

  // Threat tier + cost predictions now come from pre-computed backend snapshots.
  const conc = details.max_concentration_ug_L || 0;
  const threatTier: 1 | 2 | 3 | 4 | 5 = 3;
  const predictions: PeriodPrediction[] = [];

  // Calculate velocities
  const K = hydro.hydraulic_conductivity_ft_day;
  const n = hydro.porosity;
  const i = hydro.hydraulic_gradient;
  const darcyVelocity = K * i;
  const seepageVelocity = darcyVelocity / n;
  const annualMigration = seepageVelocity * 365;

  return {
    site_id: details.site_id,
    site_name: details.site_name,
    analysis_timestamp: new Date().toISOString(),
    latitude: details.latitude,
    longitude: details.longitude,
    threat_tier: threatTier,
    risk_summary: threatTier <= 2
      ? `High concentration (${conc.toFixed(0)} ug/L) requires immediate attention. Plume area of ${((details.plume_area_sqft || 0) / 1000).toFixed(1)}K sqft at risk of expansion.`
      : `Site monitoring recommended. Current concentration at ${conc.toFixed(0)} ug/L.`,
    predictions,
    plume_states: plumeStates,
    // PSH/LNAPL source zone parameters from database
    psh_thickness_ft: details.psh_thickness_ft ?? null,
    psh_area_sqft: details.psh_area_sqft ?? null,
    estimated_mass_lbs: details.estimated_mass_lbs ?? null,
    plume_area_sqft: details.plume_area_sqft || 0,
    max_concentration_ug_L: details.max_concentration_ug_L || 0,
    contaminant_type: details.contaminant_type || 'benzene',
    toolkit_data: {
      // Soil properties (SSURGO)
      soil_type: hydro.soil_type || 'Sandy Loam',
      aquifer_k_ft_day: hydro.hydraulic_conductivity_ft_day,
      aquifer_porosity: hydro.porosity,
      depth_to_water_ft: hydro.depth_to_water_ft || 50,
      land_use: hydro.land_use || 'Industrial',
      clay_pct: hydro.clay_pct || 0,
      sand_pct: hydro.sand_pct || 0,
      organic_matter_pct: hydro.organic_matter_pct || 0,
      organic_carbon_fraction: hydro.organic_carbon_fraction || 0,
      ksat_in_hr: hydro.ksat_in_hr || 0,
      hydrologic_group: hydro.hydrologic_group || 'B',
      // Groundwater (NWIS)
      water_level_date: hydro.water_level_date || '',
      water_elevation_ft: hydro.water_elevation_ft || 0,
      // Elevation/gradient (3DEP)
      ground_elevation_ft: hydro.ground_elevation_ft || 0,
      hydraulic_gradient: hydro.hydraulic_gradient || i,
      // Aquifer info
      aquifer_type: hydro.aquifer_type || 'unconfined',
      aquifer_name: hydro.aquifer_name || 'Unknown',
      aquifer_code: hydro.aquifer_code || '',
      // Calculated
      groundwater_velocity_ft_day: seepageVelocity,
      // Data quality
      data_sources: hydro.data_sources || [],
      confidence_score: hydro.confidence_score || 0,
    },
    physics: {
      darcy_velocity_ft_day: darcyVelocity,
      seepage_velocity_ft_day: seepageVelocity,
      annual_migration_ft: annualMigration,
      flow_direction_degrees: hydro.flow_direction_degrees,
    },
    existing_wells: details.existing_wells || [],
    recommended_wells: details.recommended_wells || [],
    total_projection_months: plumeStates.length > 0 ? plumeStates[plumeStates.length - 1].time_months : 24,
    total_estimated_cost: 0,  // From pre-computed cost snapshot
    total_wells_to_install: 0,  // From pre-computed cost snapshot
  };
}

// Threat tier labels and styles
const TIER_CONFIG: Record<number, { label: string; style: string; bgStyle: string }> = {
  1: { label: 'CRITICAL', style: 'text-mono-100', bgStyle: 'bg-mono-300' },
  2: { label: 'HIGH', style: 'text-mono-200', bgStyle: 'bg-mono-400' },
  3: { label: 'MODERATE', style: 'text-mono-300', bgStyle: 'bg-mono-500' },
  4: { label: 'LOW', style: 'text-mono-400', bgStyle: 'bg-mono-600' },
  5: { label: 'MINIMAL', style: 'text-mono-500', bgStyle: 'bg-mono-700' },
};

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(2)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(0);
}

// Calculate distance between two lat/lon points in feet
function calculateDistanceFt(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 20902231; // Earth radius in feet
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Calculate which wells are breached by plume at each time step
interface WellBreachAnalysis {
  well_id: string;
  latitude: number;
  longitude: number;
  distance_from_source_ft: number;
  breach_month: number | null;
  is_breached: boolean;
  source_database?: string;
}

function analyzeWellBreaches(
  existingWells: ExistingWell[],
  plumeStates: PlumeStateData[],
  sourceLat: number,
  sourceLon: number
): WellBreachAnalysis[] {
  return existingWells.map(well => {
    const distanceFromSource = calculateDistanceFt(sourceLat, sourceLon, well.latitude, well.longitude);

    // Check each plume state to find when well is breached
    let breachMonth: number | null = null;
    for (const state of plumeStates) {
      // Simple breach check: if plume migration distance reaches well
      const migrationDist = state.migration_distance_ft || 0;
      // Also check if well is within plume length (directional)
      const plumeLength = state.plume_length_ft || migrationDist;

      if (distanceFromSource <= plumeLength * 1.2) { // 20% buffer for lateral spread
        breachMonth = state.time_months;
        break;
      }
    }

    return {
      well_id: well.well_id,
      latitude: well.latitude,
      longitude: well.longitude,
      distance_from_source_ft: distanceFromSource,
      breach_month: breachMonth,
      is_breached: breachMonth !== null,
      source_database: well.source_database || well.source,
    };
  });
}

// DEPRECATED: Local well cost estimate. Pre-computed cost snapshots use real
// activity_unit_costs from the database with depth-tiered pricing.
function calculateRecommendedWells(
  existingWells: ExistingWell[],
  plumeStates: PlumeStateData[],
  sourceLat: number,
  sourceLon: number,
  flowDirection: number
): { month: number; wellsNeeded: number; installCost: number }[] {
  const WELL_INSTALL_COST = 15000; // Average cost per monitoring well
  const OPTIMAL_WELL_SPACING_FT = 500; // Ideal spacing between wells

  return plumeStates.map(state => {
    const plumeArea = state.plume_area_sqft || 10000;
    const plumeLength = state.plume_length_ft || Math.sqrt(plumeArea);

    // Calculate wells needed for coverage
    const coveragePerimeter = 2 * Math.sqrt(Math.PI * plumeArea); // Approximate perimeter
    const idealWellCount = Math.ceil(coveragePerimeter / OPTIMAL_WELL_SPACING_FT);

    // Count existing wells that are within plume influence zone
    const wellsInZone = existingWells.filter(w => {
      const dist = calculateDistanceFt(sourceLat, sourceLon, w.latitude, w.longitude);
      return dist <= plumeLength * 1.5;
    }).length;

    const wellsNeeded = Math.max(0, idealWellCount - wellsInZone - Math.floor(state.time_months / 12));

    return {
      month: state.time_months,
      wellsNeeded,
      installCost: wellsNeeded * WELL_INSTALL_COST,
    };
  });
}

// Tab configuration
const TABS: Tab[] = [
  { id: 'plume-2d', label: 'PLUME 2D', icon: MapIcon },
  { id: 'plume-3d', label: 'PLUME 3D', icon: MapIcon },
  { id: 'charts', label: 'ANALYTE GRAPH', icon: ChartBarIcon },
  { id: 'cost-analysis', label: 'COST ANALYSIS', icon: CurrencyDollarIcon },
  { id: 'site-data', label: 'SITE DATA', icon: Cog6ToothIcon },
  { id: 'wells', label: 'WELLS', icon: CircleStackIcon },
  { id: 'predictions', label: 'WELL PREDICTIONS', icon: SparklesIcon },
  { id: 'delineation-intelligence', label: 'DELINEATION INTEL', icon: SparklesIcon },
];

export function PolygonSiteModeling() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [site, setSite] = useState<SiteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [threePlumeResult, setThreePlumeResult] = useState<ThreePlumeResponse | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('plume-2d');
  // Multi-panel dashboard state — mirrors Phreatic
  const [viewMode, setViewMode] = useState<'solo' | 'dashboard'>('solo');
  const [dashboardPanels, setDashboardPanels] = useState<Set<TabId>>(new Set(['plume-2d']));
  const [gridCols, setGridCols] = useState<1 | 2 | 3>(2);
  // Persistent visibility state for the page-level Contaminants vs MCL legend.
  // Both the bars-only instance (top of page) and the 2D/3D map instances read
  // and write this so toggles in the legend drive both maps.
  const [visibleCompounds, setVisibleCompounds] = useState<Set<string>>(new Set());

  // Selected regulatory library — cascades through plume polygons, legend MCLs,
  // and sentinel/DW recommendations. Default is filled in by RegLibrarySelector
  // from the system default (epa-mcl).
  const [regLibrary, setRegLibrary] = useState<string>('');

  // Real-data values for the 5 contaminant cards. Refetches whenever the
  // reg-library selection changes since "lead contaminant" and "peak
  // dissolved area" depend on which MCLs we measure exceedance against.
  const [observedMetrics, setObservedMetrics] = useState<ObservedMetricsResponse | null>(null);
  const [observedError, setObservedError] = useState<string | null>(null);
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    polygonApi.getObservedMetrics(siteId, regLibrary || undefined)
      .then(resp => {
        if (cancelled) return;
        setObservedMetrics(resp);
        setObservedError(null);
      })
      .catch(err => {
        if (cancelled) return;
        const msg = err?.response?.status === 404
          ? 'The /observed-metrics endpoint isn\'t loaded — restart the API with `docker-compose up -d --force-recreate api` so it picks up the new route.'
          : (err instanceof Error ? err.message : String(err));
        console.warn('[POLYGON] observed-metrics fetch failed:', err);
        setObservedMetrics(null);
        setObservedError(msg);
      });
    return () => { cancelled = true; };
  }, [siteId, regLibrary]);

  const shouldRender = (id: TabId) =>
    (viewMode === 'solo' && activeTab === id) ||
    (viewMode === 'dashboard' && dashboardPanels.has(id));

  const togglePanel = (id: TabId) => {
    setDashboardPanels(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setViewMode('dashboard');
  };

  const goSolo = (id: TabId) => {
    setActiveTab(id);
    setViewMode('solo');
  };

  // Extracted data from database (wells, gaugings, analytical results)
  const [extractedData, setExtractedData] = useState<ExtractedDataResponse | null>(null);
  const [extractedDataLoading, setExtractedDataLoading] = useState(false);

  // TWDB wells from Texas Water Development Board
  const [twdbWells, setTwdbWells] = useState<TWDBWell[]>([]);
  const [twdbLoading, setTwdbLoading] = useState(false);
  const [twdbError, setTwdbError] = useState<string | null>(null);

  // Pre-computed cost projection snapshot
  const [costSnapshot, setCostSnapshot] = useState<CostProjectionSnapshotResponse | null>(null);

  // v3.2 cost curve — refetches when the reg library changes so MCLs reflow.
  const [v32Cost, setV32Cost] = useState<V32CostCurveResponse | null>(null);
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    fetchV32CostCurve(siteId, regLibrary || undefined)
      .then(resp => { if (!cancelled) setV32Cost(resp); })
      .catch(err => {
        if (cancelled) return;
        console.warn('[POLYGON] v3.2 cost curve fetch failed:', err);
        setV32Cost(null);
      });
    return () => { cancelled = true; };
  }, [siteId, regLibrary]);

  // Delineation-well count surfaced from the 2D map's sentinel alerts. Feeds the
  // Cost Analysis tab so the "new well installation" line uses the same count
  // the user sees on the map (rather than a separately-cached value).
  const [dwRecommendedCount, setDwRecommendedCount] = useState<number | null>(null);

  // Avg total_depth_ft of existing wells — used by Cost Analysis to infer
  // realistic per-well install cost based on actual site depths.
  const avgExistingWellDepthFt = useMemo(() => {
    const depths = (extractedData?.wells || [])
      .map(w => w.total_depth_ft)
      .filter((d): d is number => typeof d === 'number' && isFinite(d) && d > 0);
    if (depths.length === 0) return null;
    return depths.reduce((s, d) => s + d, 0) / depths.length;
  }, [extractedData]);

  // Fetch the same sentinel alerts the 2D map uses; count is the recommended
  // delineation-well number to feed into the cost calculator.
  useEffect(() => {
    if (!siteId) return;
    const today = new Date().toISOString().slice(0, 10);
    polygonApi.getSentinelAlerts(siteId, today)
      .then(resp => {
        const recs = (resp?.recommendations || []).filter((r: any) =>
          r?.trigger_well_name && !/^(plume|recovery|mdpe|src)/i.test(r.trigger_well_name)
        );
        setDwRecommendedCount(recs.length);
      })
      .catch(() => setDwRecommendedCount(null));
  }, [siteId]);

  // Page-bundle data — loaded once, drilled down to children to eliminate duplicate fetches
  const [bundledTimeline, setBundledTimeline] = useState<TimelineResponse | null | undefined>(undefined);
  const [bundledSnapshots, setBundledSnapshots] = useState<PlumeSnapshotsResponse | null | undefined>(undefined);
  const [bundledGradientMap, setBundledGradientMap] = useState<SiteGradientMap | null | undefined>(undefined);
  const [bundledContaminantBoundaries, setBundledContaminantBoundaries] = useState<ContaminantBoundaryData[] | null | undefined>(undefined);

  // Well concentration predictions
  const [wellPredictions, setWellPredictions] = useState<WellPrediction[]>([]);
  const [timelinePredictions, setTimelinePredictions] = useState<TimelinePredictionsResponse | null>(null);
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [predictionsError, setPredictionsError] = useState<string | null>(null);
  const [predictionsAnalytes, setPredictionsAnalytes] = useState<string[]>([]);
  const [selectedAnalyteFilter, setSelectedAnalyteFilter] = useState<string>('all');

  // Get navigation state passed from PolygonUpload
  const navState = location.state as NavigationState | null;

  // Helper to detect if coordinates are in relative 0-1 range vs absolute lat/lon
  const detectCoordinatesType = (contours?: { elevation_ft: number; coordinates: [number, number][] }[]): 'absolute' | 'relative' => {
    if (!contours || contours.length === 0) return 'absolute';
    // Check first contour's coordinates
    const firstContour = contours[0];
    if (!firstContour.coordinates || firstContour.coordinates.length === 0) return 'absolute';

    let allInRelativeRange = true;
    let anyLooksLikeRealLatLon = false;

    for (const [lat, lon] of firstContour.coordinates) {
      // Check if values are clearly in relative 0-1 range
      if (lat < -0.1 || lat > 1.1 || lon < -0.1 || lon > 1.1) {
        allInRelativeRange = false;
      }
      // Check if values look like real-world lat/lon coordinates
      const looksLikeLatitude = Math.abs(lat) > 10 && Math.abs(lat) <= 90;
      const looksLikeLongitude = Math.abs(lon) > 10 && Math.abs(lon) <= 180;
      if (looksLikeLatitude || looksLikeLongitude) {
        anyLooksLikeRealLatLon = true;
      }
    }

    // If any coordinate looks like real lat/lon, treat as absolute
    if (anyLooksLikeRealLatLon) {
      return 'absolute';
    }
    // If all coordinates are in 0-1 range, treat as relative
    return allInRelativeRange ? 'relative' : 'absolute';
  };

  // Convert extracted gradient data from navigation state to SiteGradientMap format
  const extractedGradientMap: SiteGradientMap | null = React.useMemo(() => {
    if (!navState?.extractedGradientData) {
      return null;
    }
    const gd = navState.extractedGradientData;
    // Use API-provided coordinates_type if available, otherwise detect from data
    const coordinatesType = gd.coordinates_type || detectCoordinatesType(gd.contours);
    return {
      id: `extracted-${Date.now()}`,
      site_id: siteId || 'unknown',
      source: 'extracted' as const,
      contours: gd.contours || [],
      flow_arrows: gd.flow_arrows,
      well_readings: gd.well_readings,
      average_gradient: gd.average_gradient,
      average_flow_direction_deg: gd.average_flow_direction_deg,
      coordinates_type: coordinatesType,
      map_north_direction: gd.map_north_direction,
      has_gradient_grid: false,
    };
  }, [navState?.extractedGradientData, siteId]);

  // Convert extracted plume boundaries from navigation state
  const extractedPlumeBoundaries = React.useMemo(() => {
    if (!navState?.extractedPlumeBoundaries || navState.extractedPlumeBoundaries.length === 0) {
      return null;
    }
    return navState.extractedPlumeBoundaries;
  }, [navState?.extractedPlumeBoundaries]);

  // Convert extracted well inventory from navigation state or site.existing_wells
  const extractedWellInventory = React.useMemo(() => {
    // Priority 1: Use navigation state if available
    if (navState?.extractedWellInventory && navState.extractedWellInventory.length > 0) {
      return navState.extractedWellInventory;
    }

    // Priority 2: Use site.existing_wells (from API response) if available
    if (site?.existing_wells && site.existing_wells.length > 0) {
      const wellInventory = site.existing_wells.map(w => ({
        well_id: w.well_id,
        // Normalize well_type: API sends 'monitor', ThreePlumeVisualization accepts both 'monitor' and 'monitoring'
        well_type: w.well_type || 'monitor',
        lat: w.latitude,
        lon: w.longitude,
        status: w.status || 'active',
        depth_ft: w.well_depth_ft || (w as { depth_ft?: number }).depth_ft,
      }));
      const recovery = wellInventory.filter(w => w.well_type === 'recovery').length;
      const mdpe = wellInventory.filter(w => w.well_type === 'mdpe').length;
      // Count both 'monitor' and 'monitoring' as monitoring wells
      const monitoring = wellInventory.filter(w => w.well_type === 'monitoring' || w.well_type === 'monitor').length;
      return wellInventory;
    }

    return null;
  }, [navState?.extractedWellInventory, site?.existing_wells]);

  // Handle site deletion
  const handleDeleteSite = async () => {
    if (!siteId) return;

    try {
      setDeleting(true);
      await polygonApi.deleteSite(siteId);
      navigate('/polygon/sites', { replace: true });
    } catch (err) {
      console.error('Failed to delete site:', err);
      setError('Failed to delete site');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  // Handle re-running simulation with recovery/MDPE wells
  const handleRerunWithWells = useCallback(async (recoveryWells: RecoveryWell[], mdpeWells: MDPEWell[]) => {
    if (!site) return;

    // Get existing three-plume result to extract PSH parameters
    const existingResult = (threePlumeResult || navState?.threePlumeResult) as ThreePlumeResponse | undefined;

    try {
      setIsSimulating(true);

      // Build request - use site database values if available, otherwise let backend fetch
      // Pass undefined to let backend auto-populate from vault/gauging data
      const pshThickness = site.psh_thickness_ft
        ?? (navState?.combinedAnalysis?.psh_thickness_ft as number | undefined)
        ?? existingResult?.plume_states?.[0]?.psh?.thickness_ft
        ?? undefined;  // Let backend fetch from vault
      const pshArea = site.psh_area_sqft
        ?? navState?.psh_area_sqft
        ?? existingResult?.plume_states?.[0]?.psh?.area_sqft
        ?? undefined;  // Let backend fetch from vault

      const request: ThreePlumeRequest = {
        // PSH parameters - pass undefined to let backend fetch from vault/gauging
        psh_thickness_ft: pshThickness,
        psh_area_sqft: pshArea,
        contaminants: navState?.contaminants,
        contaminant_type: site.contaminant_type || navState?.contaminant_type || existingResult?.contaminant_type || 'benzene',

        // Hydrogeology from site data
        hydraulic_conductivity_ft_day: site.toolkit_data?.aquifer_k_ft_day,
        porosity: site.toolkit_data?.aquifer_porosity,
        depth_to_water_ft: site.toolkit_data?.depth_to_water_ft,

        // Remediation wells
        recovery_wells: recoveryWells.map(w => ({
          x_ft: w.x_ft,
          y_ft: w.y_ft,
          pump_rate_gpd: w.pump_rate_gpd,
          start_month: w.start_month,
        })),
        mdpe_wells: mdpeWells.map(w => ({
          x_ft: w.x_ft,
          y_ft: w.y_ft,
          extraction_cfm: w.extraction_cfm,
          start_month: w.start_month,
        })),

        projection_months: site.total_projection_months || 24,
      };

      const result = await polygonApi.runThreePlumeSimulation(request);
      setThreePlumeResult(result);
    } catch (err) {
      console.error('Failed to re-run simulation with wells:', err);
      setError('Failed to re-run simulation with wells');
    } finally {
      setIsSimulating(false);
    }
  }, [site, threePlumeResult, navState]);

  // ---------------------------------------------------------------------------
  // UNIFIED DATA LOADING
  // Fetch site details + extracted data in PARALLEL, then kick off simulation.
  // Previously these were 3 sequential useEffects creating a waterfall:
  //   getSiteDetails → wait → getSiteExtractedData → wait → runSimulation
  // Now: getSiteDetails + getSiteExtractedData run simultaneously, then simulation.
  // ---------------------------------------------------------------------------
  const [simulationLoading, setSimulationLoading] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    const id = siteId; // narrow to string for async closure
    let cancelled = false;

    async function loadSiteData() {
      setLoading(true);
      setExtractedDataLoading(true);

      try {
        // PRIORITY 1: Use navigation state if available (passed from upload form)
        if (navState?.combinedAnalysis) {
          const transformedData = transformNavigationState(navState);
          if (!cancelled) {
            setSite(transformedData);
            setLoading(false);
          }
          // Still fetch extracted data for Wells tab + bundle for child data
          try {
            const [extracted, bundle] = await Promise.all([
              polygonApi.getSiteExtractedData(id),
              polygonApi.getPageBundle(id).catch(() => null),
            ]);
            if (!cancelled) {
              setExtractedData(extracted);
              setExtractedDataLoading(false);
              if (bundle) {
                setCostSnapshot(bundle.cost_projection);
                setBundledTimeline(bundle.timeline);
                setBundledSnapshots(bundle.plume_snapshots);
                setBundledGradientMap(bundle.gradient_map);
                // Extract latest snapshot boundaries for initial display
                const snaps = bundle.plume_snapshots?.snapshots;
                if (snaps && snaps.length > 0) {
                  const latest = snaps[snaps.length - 1]; // sorted by date asc
                  setBundledContaminantBoundaries(latest.compound_boundaries || null);
                } else {
                  setBundledContaminantBoundaries(null); // No snapshots — let component fall through to API
                }
              }
            }
          } catch { /* Non-critical */ }
          return;
        }

        // PRIORITY 2: Fetch page-bundle + extracted data IN PARALLEL
        // page-bundle consolidates: site_details, cost_projection, plume_snapshots,
        // timeline, psh_wells, and gradient_map into a single DB transaction (~1-2s).
        const [bundle, extracted] = await Promise.all([
          polygonApi.getPageBundle(id),
          polygonApi.getSiteExtractedData(id),
        ]);

        if (cancelled) return;

        // Extract site details from bundle (replaces separate getSiteDetails call)
        if (bundle.site_details) {
          const siteDetails = transformSiteDetails(bundle.site_details);
          setSite(siteDetails);
        } else {
          // Minimal site stub so UI doesn't show "SITE NOT FOUND"
          setSite({ id, name: bundle.site_name || 'Unknown Site' } as SiteData);
        }
        setLoading(false);
        setExtractedData(extracted);
        setExtractedDataLoading(false);

        // Distribute bundled data to state for prop-drilling to children
        setCostSnapshot(bundle.cost_projection);
        setBundledTimeline(bundle.timeline);
        setBundledSnapshots(bundle.plume_snapshots);
        setBundledGradientMap(bundle.gradient_map);
        // Extract latest snapshot boundaries for initial display (skips 22s live computation)
        const snaps = bundle.plume_snapshots?.snapshots;
        if (snaps && snaps.length > 0) {
          const latest = snaps[snaps.length - 1]; // sorted by date asc
          setBundledContaminantBoundaries(latest.compound_boundaries || null);
        } else {
          setBundledContaminantBoundaries(null); // No snapshots — let component fall through to API
        }

        // Now kick off three-plume simulation if needed
        if (navState?.threePlumeResult) return; // Already have it from navState

        const contaminantMapping: Record<string, string> = {
          'benzene': 'benzene',
          'toluene': 'toluene',
          'ethylbenzene': 'ethylbenzene',
          'xylene': 'xylenes',
          'xylenes': 'xylenes',
          'tph': 'tph_gro',
          'mtbe': 'mtbe',
        };

        const detectedParams = extracted.summary?.parameters_detected || [];
        const contaminants = detectedParams
          .filter((p: string) => contaminantMapping[p.toLowerCase()])
          .map((p: string) => ({
            type: contaminantMapping[p.toLowerCase()],
            concentration_ug_l: extracted.summary?.max_concentration_ug_l || 500,
          }));

        if (contaminants.length === 0) return;

        setSimulationLoading(true);
        const siteData = bundle.site_details;
        const result = await polygonApi.runThreePlumeSimulation({
          site_id: id,
          psh_thickness_ft: siteData?.psh_thickness_ft ?? undefined,
          psh_area_sqft: siteData?.psh_area_sqft ?? undefined,
          contaminants,
          projection_months: 60,
          remediation_type: 'none',
        });

        if (!cancelled) {
          setThreePlumeResult(result);
        }
      } catch (err) {
        console.error('Failed to load site data:', err);
        if (!cancelled) setError('Failed to load site data');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setExtractedDataLoading(false);
          setSimulationLoading(false);
        }
      }
    }

    loadSiteData();
    return () => { cancelled = true; };
  }, [siteId, navState]);

  // Fetch TWDB wells based on site lat/lon
  useEffect(() => {
    async function fetchTWDBWells() {
      if (!site?.latitude || !site?.longitude) return;
      if (twdbWells.length > 0) return;

      setTwdbLoading(true);
      setTwdbError(null);
      try {
        const response = await polygonApi.queryTWDBWells(site.latitude, site.longitude, 1.0);
        setTwdbWells(response.wells || []);
      } catch (err) {
        console.error('[POLYGON] Failed to fetch TWDB wells:', err);
        setTwdbError('Failed to load TWDB wells');
      } finally {
        setTwdbLoading(false);
      }
    }

    fetchTWDBWells();
  }, [site?.latitude, site?.longitude, twdbWells.length]);

  // Fetch well concentration predictions (single-horizon snapshot + full
  // timeline). If the DB doesn't contain a complete v3.2 horizon curve
  // (BTEX 6 horizons, TPH 3 horizons), automatically run inference once so
  // the Well Predictions tab and the Analyte Graph both have data to show.
  //
  // Reruns whenever the reg-library selection changes — the backend re-scores
  // mcl_mgl + exceedance against the chosen library so the dropdown is
  // reactive without re-running inference.
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;

    async function fetchAndBackfill() {
      setPredictionsLoading(true);
      setPredictionsError(null);

      const lib = regLibrary || undefined;
      const fetchBoth = async () => {
        const [single, timeline] = await Promise.all([
          polygonApi.getWellPredictions(siteId!, 1, 200, undefined, undefined, lib),
          polygonApi.getTimelinePredictions(siteId!, lib).catch(() => null),
        ]);
        return { single, timeline };
      };

      try {
        let { single, timeline } = await fetchBoth();

        // BTEX should have all 6 calendar horizons. If the DB only has a
        // subset (e.g. only the 3mo row from the legacy single-horizon path),
        // run multi-horizon inference once to fill the rest. Surface the
        // result so silent failures (missing artifacts, etc.) don't leave
        // the user staring at em-dashes.
        const horizonDaysSet = new Set(
          (timeline?.months || []).map(m => m.horizon_days ?? null).filter(Boolean) as number[],
        );
        const btexCompleteSet = [90, 365, 730, 1825, 3650, 7300];
        const btexMissing = btexCompleteSet.some(d => !horizonDaysSet.has(d));

        if (btexMissing && !cancelled) {
          try {
            const result = await polygonApi.generatePredictions(siteId!, 40);
            const refreshed = await fetchBoth();
            single = refreshed.single;
            timeline = refreshed.timeline;
            const refreshedDays = new Set(
              (timeline?.months || []).map(m => m.horizon_days ?? null).filter(Boolean) as number[],
            );
            const stillMissing = btexCompleteSet.filter(d => !refreshedDays.has(d));
            if (stillMissing.length > 0) {
              setPredictionsError(
                `Inference returned ${result?.predictions ?? 0} rows but horizons [${stillMissing.join(', ')}] days are still missing. ` +
                'Check the API logs (docker-compose logs api | tail -50) for v3.2 ensemble load errors.',
              );
            }
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            console.error('[POLYGON] Auto-trigger generatePredictions failed:', err);
            setPredictionsError(
              `Could not run multi-horizon inference: ${msg}. ` +
              'Verify model_artifacts/concentration/3.2.0/ is mounted into the API container and restart with docker-compose restart api.',
            );
          }
        }

        if (cancelled) return;
        setWellPredictions(single.predictions || []);
        setPredictionsAnalytes(single.analytes || []);
        setTimelinePredictions(timeline);
      } catch (err) {
        if (!cancelled) {
          console.error('[POLYGON] Failed to fetch well predictions:', err);
          setPredictionsError('No predictions available. Click "Generate Predictions" to create forecasts.');
        }
      } finally {
        if (!cancelled) setPredictionsLoading(false);
      }
    }

    fetchAndBackfill();
    return () => { cancelled = true; };
  }, [siteId, regLibrary]);

  // Combine well data with latest gauging and sampling for display
  const wellsWithData = React.useMemo(() => {
    if (!extractedData?.wells) return [];

    return extractedData.wells.map((well) => {
      // Find latest gauging for this well
      const wellGaugings = (extractedData.gaugings || [])
        .filter(g => g.well_id === well.well_id)
        .sort((a, b) => (b.gauge_date || '').localeCompare(a.gauge_date || ''));
      const latestGauging = wellGaugings[0] || null;

      // Find latest analytical results for this well (group by compound)
      const wellAnalytical = (extractedData.analytical_results || [])
        .filter(a => a.well_id === well.well_id)
        .sort((a, b) => (b.sample_date || '').localeCompare(a.sample_date || ''));

      // Get unique compounds with their latest concentration
      const latestByCompound: Record<string, ExtractedAnalyticalResult> = {};
      for (const result of wellAnalytical) {
        const compound = result.parameter?.toLowerCase() || 'unknown';
        if (!latestByCompound[compound]) {
          latestByCompound[compound] = result;
        }
      }

      return {
        ...well,
        latestGauging,
        latestAnalytical: Object.values(latestByCompound),
        gaugingCount: wellGaugings.length,
        analyticalCount: wellAnalytical.length,
      };
    });
  }, [extractedData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <ArrowPathIcon className="h-8 w-8 text-mono-500 animate-spin mx-auto mb-4" />
          <p className="text-mono-500 text-sm tracking-wider">LOADING ANALYSIS...</p>
        </div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <ExclamationTriangleIcon className="h-12 w-12 text-mono-500 mb-4" />
        <h2 className="text-mono-200 text-lg tracking-wider mb-2">SITE NOT FOUND</h2>
        <p className="text-mono-500 text-sm mb-6">{error || 'The requested site could not be loaded.'}</p>
        <Link
          to="/polygon/sites"
          className="flex items-center gap-2 px-4 py-2 bg-mono-800 hover:bg-mono-700 text-mono-300 text-xs tracking-wider transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          BACK TO SITES
        </Link>
      </div>
    );
  }

  // Cost data from pre-computed backend snapshots
  const hasPrecomputedCosts = costSnapshot?.precomputed === true;
  const snapshotTier = hasPrecomputedCosts && costSnapshot?.projection?.threat_tier
    ? costSnapshot.projection.threat_tier as 1 | 2 | 3 | 4 | 5
    : site.threat_tier;
  const tierConfig = TIER_CONFIG[snapshotTier] || TIER_CONFIG[3];

  const currentCost = costSnapshot?.current_cost ?? 0;
  const cost6mo = costSnapshot?.cost_6mo ?? 0;
  const cost12mo = costSnapshot?.cost_12mo ?? 0;
  const cost24mo = costSnapshot?.cost_24mo ?? 0;

  // Get selected month for breach analysis
  const selectedMonth = 6;

  // Analyze well breaches based on plume migration
  const wellBreachAnalysis = analyzeWellBreaches(
    site.existing_wells || [],
    site.plume_states || [],
    site.latitude,
    site.longitude
  );

  // Calculate wells at risk at selected month
  const wellsBreachedByMonth = wellBreachAnalysis.filter(w =>
    w.breach_month !== null && w.breach_month <= selectedMonth
  );
  const wellsAtRisk = wellBreachAnalysis.filter(w =>
    w.breach_month !== null && w.breach_month > selectedMonth
  );
  const wellsSafe = wellBreachAnalysis.filter(w => w.breach_month === null);

  // Calculate new wells recommendations
  const recommendedWellsPlan = calculateRecommendedWells(
    site.existing_wells || [],
    site.plume_states || [],
    site.latitude,
    site.longitude,
    site.physics?.flow_direction_degrees || 180
  );

  // Get recommended wells for current period
  const currentRecommendedWells = recommendedWellsPlan.find(p => p.month === selectedMonth);
  const totalNewWellsCost = recommendedWellsPlan.reduce((sum, p) => sum + p.installCost, 0);

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* ── Panel Sidebar (matches Phreatic styling) ── */}
      <aside className="w-48 flex-shrink-0 bg-mono-950 border-r border-mono-800 overflow-y-auto">
        <div className="p-2">
          <div className="px-2 mb-2 space-y-1.5">
            <span className="text-[10px] text-mono-500 uppercase tracking-wider">Panels</span>
            <div className="flex items-center gap-1 bg-mono-900 rounded p-0.5">
              {([1, 2, 3] as const).map(n => (
                <button key={n} onClick={() => { setGridCols(n); setViewMode('dashboard'); }}
                  className={`flex-1 text-[9px] py-0.5 rounded transition-colors ${
                    gridCols === n && viewMode === 'dashboard' ? 'bg-cyan-500/20 text-cyan-300' : 'text-mono-500 hover:text-mono-300'
                  }`}
                  title={`${n} per row`}
                >
                  {n === 1 ? '☰' : n === 2 ? '▇▇' : '▇▇▇'}
                </button>
              ))}
            </div>
          </div>
          {TABS.map(tab => (
            <PolygonSidebarTab
              key={tab.id}
              label={tab.label}
              isSolo={viewMode === 'solo' && activeTab === tab.id}
              isChecked={dashboardPanels.has(tab.id)}
              viewMode={viewMode}
              onToggle={() => togglePanel(tab.id)}
              onSolo={() => goSolo(tab.id)}
            />
          ))}
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex items-start justify-between">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-mono-500 hover:text-mono-300 text-xs tracking-wider mb-4 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              BACK
            </button>
            <div className="flex items-center gap-4">
              <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full text-lg font-bold ${tierConfig.bgStyle} text-mono-950`}>
                {snapshotTier}
              </span>
              <div>
                <h1 className="text-2xl font-bold text-mono-100 tracking-wider font-orbitron">{site.site_name}</h1>
                <p className="text-mono-500 text-sm mt-1">
                  Site ID: {site.site_id} | Analysis: {new Date(site.analysis_timestamp).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className={`inline-block px-3 py-1 rounded text-xs tracking-wider ${tierConfig.bgStyle} text-mono-950`}>
              {hasPrecomputedCosts ? 'COST URGENCY' : 'TIER'} {snapshotTier} - {tierConfig.label}
            </span>
          </div>
        </div>
      </FadeIn>


      {/* Top-of-page: three vertical columns — Contaminant / Cost / Reg Library.
          The Contaminants vs MCL legend dropdown stays as a separate row below. */}
      {observedError && (
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-2 text-[11px] text-amber-300">
          <span className="font-semibold uppercase tracking-wider text-amber-400">Contaminant cards using simulation:</span>{' '}
          {observedError}
        </div>
      )}
      <FadeIn delay={120}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          {/* Column 1: Contaminant stats (vertical stack of 5 cards) */}
          <ContaminantStatsColumn
            threePlumeResult={(threePlumeResult || navState?.threePlumeResult) as ThreePlumeResponse | undefined}
            observedMetrics={observedMetrics}
          />

          {/* Columns 2 + 3 share a flex group with `self-start` so they DON'T
              stretch to the outer grid row height. Inside the flex group,
              flex defaults stretch col 3 to match col 2's natural height —
              so the Group Avg vs MCL card ends at the same y as col 2's
              Delineation card. Cols 1 + 4 keep grid-default stretch and run
              to the tallest column. */}
          <div className="col-span-2 flex gap-3 self-start">
          <div className="flex-1 space-y-2">
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] text-mono-500 uppercase tracking-wider">Start Now</div>
                {v32Cost && (
                  <span className="text-[8px] text-mono-500 uppercase">
                    {v32Cost.dominant_group}
                  </span>
                )}
              </div>
              <div className="text-sm font-semibold text-mono-100 font-orbitron">
                {formatCurrency(v32Cost?.cards.start_now ?? 0)}
              </div>
              <div className="text-[9px] text-mono-500 mt-0.5">
                {v32Cost?.curve.find(p => p.label === 'now')?.wells_hot ?? 0} wells exceeding
              </div>
            </div>
            <div className="bg-mono-900 border border-red-500/30 rounded-lg p-3">
              <div className="text-[10px] text-red-400 uppercase tracking-wider">
                {v32Cost?.liability_horizon_label ?? '20yr'} Liability
              </div>
              <div className="text-sm font-semibold text-red-300 font-orbitron">
                {formatCurrency(v32Cost?.cards.liability ?? 0)}
              </div>
              <div className="text-[9px] text-mono-500 mt-0.5">
                {(() => {
                  const hot = v32Cost
                    ? Object.values(v32Cost.hotspot_overlay).filter(o => o.exceeds_at_max_horizon).length
                    : 0;
                  return `${hot} wells still hot at ${v32Cost?.liability_horizon_label ?? '20yr'}`;
                })()}
              </div>
            </div>
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
              <div className="text-[10px] text-mono-500 uppercase tracking-wider">Delineation</div>
              <div className="text-sm font-semibold text-mono-100 font-orbitron">
                {formatCurrency(v32Cost?.cards.delineation_cost ?? 0)}
              </div>
              <div className="text-[9px] text-mono-500 mt-0.5">
                {v32Cost?.num_delineation_wells ?? 0} DW recommended
              </div>
            </div>
          </div>

          {/* Column 3: BTEX + TPH avg-conc/MCL trajectory. Each group renders
              on its OWN full-width SVG so both lines have the same visual
              length but represent different time scales. Always renders the
              full horizon X-axis — empty slots reveal where v3.2 had no data
              (long-horizon TPH bundles are do_not_use, and any per-horizon
              predict failure leaves a gap). */}
          <div className="bg-mono-900 border border-amber-500/30 rounded-lg p-3 space-y-3 flex-1">
            <div className="flex items-center justify-between">
              <div className="text-[10px] text-amber-400 uppercase tracking-wider">Group Avg vs MCL</div>
              {v32Cost?.library_slug && (
                <span className="text-[8px] text-mono-500 uppercase">{v32Cost.library_slug}</span>
              )}
            </div>
            {(() => {
              // Static reference horizons — each group rendered on its own
              // time-proportional X-axis so visual position matches real time.
              // BTEX: 0 → 7300 days (20yr) across the full width.
              // TPH:  0 → 730 days (2yr) across the full width.
              const BTEX_HORIZONS: Array<{ label: string; days: number }> = [
                { label: '3mo', days: 90 },
                { label: '1yr', days: 365 },
                { label: '2yr', days: 730 },
                { label: '5yr', days: 1825 },
                { label: '10yr', days: 3650 },
                { label: '20yr', days: 7300 },
              ];
              const TPH_HORIZONS: Array<{ label: string; days: number }> = [
                { label: '3mo', days: 90 },
                { label: '1yr', days: 365 },
                { label: '2yr', days: 730 },
              ];
              const SVG_W = 300;
              const SVG_H = 50;
              const renderTraj = (
                groupLabel: string,
                traj: { horizons: string[]; ratios: (number | null)[] } | undefined,
                fullHorizons: Array<{ label: string; days: number }>,
                color: string,
              ) => {
                const maxDays = fullHorizons[fullHorizons.length - 1].days;
                // Map each label to its ratio (null if missing in the response)
                const ratioByLabel = new Map<string, number | null>();
                (traj?.horizons ?? []).forEach((h, i) => {
                  const r = traj?.ratios[i];
                  ratioByLabel.set(h, typeof r === 'number' ? r : null);
                });
                const pts = fullHorizons.map(h => {
                  const r = ratioByLabel.get(h.label) ?? null;
                  return {
                    x: (h.days / maxDays) * SVG_W,   // time-proportional
                    ratio: r,
                    label: h.label,
                  };
                });
                const valid = pts.filter((p): p is { x: number; ratio: number; label: string } =>
                  typeof p.ratio === 'number',
                );
                if (valid.length < 1) {
                  return (
                    <div className="text-[9px] text-mono-600">
                      <span className="font-semibold mr-1">{groupLabel}:</span>no predictions
                    </div>
                  );
                }
                const minV = Math.min(...valid.map(p => p.ratio));
                const maxV = Math.max(...valid.map(p => p.ratio));
                const range = maxV - minV || 1;
                const ptsXY = pts.map(p => ({
                  x: p.x,
                  y: p.ratio == null ? null : (1 - (p.ratio - minV) / range) * (SVG_H - 4) + 2,
                  label: p.label,
                  ratio: p.ratio,
                }));
                const polylineSegments: string[] = [];
                let current: { x: number; y: number }[] = [];
                for (const p of ptsXY) {
                  if (p.y == null) {
                    if (current.length >= 2) {
                      polylineSegments.push(current.map(pt => `${pt.x},${pt.y}`).join(' '));
                    }
                    current = [];
                  } else {
                    current.push({ x: p.x, y: p.y });
                  }
                }
                if (current.length >= 2) {
                  polylineSegments.push(current.map(pt => `${pt.x},${pt.y}`).join(' '));
                }
                const mclY = (minV <= 1.0 && maxV >= 1.0)
                  ? (1 - (1.0 - minV) / range) * (SVG_H - 4) + 2
                  : null;
                const tooltip = ptsXY
                  .map(p => `${p.label}: ${p.ratio == null ? '—' : p.ratio.toFixed(2) + '×MCL'}`)
                  .join(' · ');
                const lastValid = [...ptsXY].reverse().find(p => p.ratio != null);
                return (
                  <div>
                    <div className="flex items-center justify-between text-[10px] text-mono-400">
                      <span className="font-semibold">{groupLabel}</span>
                      {lastValid && (
                        <span>{lastValid.ratio!.toFixed(2)}×MCL @ {lastValid.label}</span>
                      )}
                    </div>
                    <svg width={SVG_W} height={SVG_H} title={`${groupLabel} avg conc/MCL — ${tooltip}`}>
                      {mclY != null && (
                        <>
                          <line
                            x1={0} y1={mclY} x2={SVG_W} y2={mclY}
                            stroke="#888" strokeWidth={0.5} strokeDasharray="2 2"
                          />
                          <text x={2} y={mclY - 2} fontSize={7} fill="#888">MCL</text>
                        </>
                      )}
                      {polylineSegments.map((seg, i) => (
                        <polyline key={i} points={seg} fill="none" stroke={color} strokeWidth={1.5} />
                      ))}
                      {ptsXY.map((p, i) =>
                        p.ratio != null && p.y != null ? (
                          <circle key={i} cx={p.x} cy={p.y} r={2} fill={color} />
                        ) : (
                          <circle key={i} cx={p.x} cy={SVG_H / 2} r={1} fill="#444" />
                        )
                      )}
                    </svg>
                    {/* Labels positioned absolutely at their time-proportional X so the
                        tick aligns with the data point above it. */}
                    <div className="relative h-3" style={{ width: SVG_W }}>
                      {ptsXY.map((p, i) => (
                        <span
                          key={i}
                          className={`absolute text-[8px] text-mono-600 ${p.ratio == null ? 'opacity-40' : ''}`}
                          style={{
                            left: `${(p.x / SVG_W) * 100}%`,
                            transform:
                              i === 0 ? 'translateX(0)' :
                              i === ptsXY.length - 1 ? 'translateX(-100%)' :
                              'translateX(-50%)',
                          }}
                        >
                          {p.label}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              };
              return (
                <>
                  {renderTraj('BTEX → 20yr', v32Cost?.btex_trajectory, BTEX_HORIZONS, '#f59e0b')}
                  {renderTraj('TPH → 2yr', v32Cost?.tph_trajectory, TPH_HORIZONS, '#06b6d4')}
                </>
              );
            })()}
          </div>
          </div>{/* /col-2+3 flex group */}

          {/* Column 4: Reg library selector + key MCLs stacked vertically */}
          <RegLibrarySelector value={regLibrary} onChange={setRegLibrary} />
        </div>
      </FadeIn>

      {/* Persistent contaminant stats + MCL legend (mirrors Phreatic's site-stats bar).
          Always visible regardless of which tab is active. Toggling a compound here
          updates the active 2D and 3D map panes via the controlled visibleCompounds. */}
      <FadeIn delay={175}>
        <ThreePlumeVisualization
          paneMode="bars-only"
          controlledVisibleCompounds={visibleCompounds}
          onVisibleCompoundsChange={setVisibleCompounds}
          centerLat={site.latitude}
          centerLon={site.longitude}
          threePlumeResult={(threePlumeResult || navState?.threePlumeResult) as ThreePlumeResponse | undefined}
          flowDirection={site.physics?.flow_direction_degrees || 180}
          siteName={site.site_name}
          siteId={siteId}
          extractedGradientMap={extractedGradientMap}
          extractedPlumeBoundaries={extractedPlumeBoundaries}
          extractedWellInventory={extractedWellInventory}
          depthToWaterFt={site.toolkit_data?.depth_to_water_ft}
          aquiferThicknessFt={30}
          isSimulating={isSimulating}
          simulationLoading={simulationLoading}
          bundledGradientMap={bundledGradientMap ?? null}
          bundledTimeline={bundledTimeline}
          bundledSnapshots={bundledSnapshots}
          bundledContaminantBoundaries={bundledContaminantBoundaries}
        />
      </FadeIn>

      {/* Tab Content (panels render solo or as a grid in dashboard mode) */}
      <div className={`min-h-[600px] ${
        viewMode === 'dashboard'
          ? `grid gap-4 ${gridCols === 1 ? 'grid-cols-1' : gridCols === 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3'}`
          : 'space-y-6'
      }`}>
        {/* Tab 1: PLUME 2D — satellite/map view of the dissolved plume */}
        {shouldRender('plume-2d') && (
          <FadeIn delay={200}>
            <ThreePlumeVisualization
              paneMode="2d-only"
              controlledVisibleCompounds={visibleCompounds}
              onVisibleCompoundsChange={setVisibleCompounds}
              regLibrary={regLibrary}
              centerLat={site.latitude}
              centerLon={site.longitude}
              threePlumeResult={(threePlumeResult || navState?.threePlumeResult) as ThreePlumeResponse | undefined}
              flowDirection={site.physics?.flow_direction_degrees || 180}
              siteName={site.site_name}
              siteId={siteId}
              extractedGradientMap={extractedGradientMap}
              extractedPlumeBoundaries={extractedPlumeBoundaries}
              extractedWellInventory={extractedWellInventory}
              depthToWaterFt={site.toolkit_data?.depth_to_water_ft}
              aquiferThicknessFt={30}
              onRerunWithWells={handleRerunWithWells}
              isSimulating={isSimulating}
              simulationLoading={simulationLoading}
              bundledGradientMap={bundledGradientMap ?? null}
              bundledTimeline={bundledTimeline}
              bundledSnapshots={bundledSnapshots}
              bundledContaminantBoundaries={bundledContaminantBoundaries}
            />
          </FadeIn>
        )}

        {/* Tab 1b: PLUME 3D — vertical cross-section / depth view */}
        {shouldRender('plume-3d') && (
          <FadeIn delay={200}>
            <ThreePlumeVisualization
              paneMode="3d-only"
              controlledVisibleCompounds={visibleCompounds}
              onVisibleCompoundsChange={setVisibleCompounds}
              regLibrary={regLibrary}
              centerLat={site.latitude}
              centerLon={site.longitude}
              threePlumeResult={(threePlumeResult || navState?.threePlumeResult) as ThreePlumeResponse | undefined}
              flowDirection={site.physics?.flow_direction_degrees || 180}
              siteName={site.site_name}
              siteId={siteId}
              extractedGradientMap={extractedGradientMap}
              extractedPlumeBoundaries={extractedPlumeBoundaries}
              extractedWellInventory={extractedWellInventory}
              depthToWaterFt={site.toolkit_data?.depth_to_water_ft}
              aquiferThicknessFt={30}
              onRerunWithWells={handleRerunWithWells}
              isSimulating={isSimulating}
              simulationLoading={simulationLoading}
              bundledGradientMap={bundledGradientMap ?? null}
              bundledTimeline={bundledTimeline}
              bundledSnapshots={bundledSnapshots}
              bundledContaminantBoundaries={bundledContaminantBoundaries}
            />
          </FadeIn>
        )}

        {/* Tab 2: Charts — analyte concentration history per well, all on one chart */}
        {shouldRender('charts') && (
          <FadeIn delay={200}>
            <AnalyteHistoryChart
              siteId={siteId!}
              visibleCompounds={visibleCompounds}
              onVisibleCompoundsChange={setVisibleCompounds}
            />
          </FadeIn>
        )}

        {/* Tab 3: Cost Analysis - Cost Projection Timeline */}
        {shouldRender('cost-analysis') && (
          <CostAnalysisTab
            siteId={siteId!}
            costSnapshot={costSnapshot}
            hasPrecomputedCosts={hasPrecomputedCosts}
            cost24mo={cost24mo}
            totalProjectionMonths={site.total_projection_months}
            recommendedDelineationWellCount={dwRecommendedCount ?? undefined}
            avgExistingWellDepthFt={avgExistingWellDepthFt ?? undefined}
          />
        )}

        {/* Tab 4: Site Data - Hydrogeology and Flow Analysis */}
        {shouldRender('site-data') && (
          <div className="space-y-6">
            <SiteDataTab
              toolkitData={site.toolkit_data}
              physics={site.physics}
              contaminants={navState?.contaminants}
            />
            <CouplingPhysicsCard />
          </div>
        )}

        {/* Tab 5: Wells - Complete Well Database with Gauging and Sampling */}
        {shouldRender('wells') && (
          <FadeIn delay={200}>
            <div className="space-y-6">
              {/* Well Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-mono-900 rounded-lg p-4 border border-mono-800">
                  <p className="text-mono-500 text-xs tracking-wider mb-1">TOTAL WELLS</p>
                  <p className="text-2xl font-bold text-cyan-400 font-orbitron">{extractedData?.wells?.length || site.existing_wells?.length || 0}</p>
                  <p className="text-mono-600 text-xs mt-1">in database</p>
                </div>
                <div className="bg-mono-900 rounded-lg p-4 border border-mono-800">
                  <p className="text-mono-500 text-xs tracking-wider mb-1">GAUGING RECORDS</p>
                  <p className="text-2xl font-bold text-blue-400 font-orbitron">{extractedData?.summary?.total_gaugings || 0}</p>
                  <p className="text-mono-600 text-xs mt-1">
                    {extractedData?.summary?.latest_gauging_date
                      ? `Latest: ${new Date(extractedData.summary.latest_gauging_date).toLocaleDateString()}`
                      : 'No data'}
                  </p>
                </div>
                <div className="bg-mono-900 rounded-lg p-4 border border-mono-800">
                  <p className="text-mono-500 text-xs tracking-wider mb-1">ANALYTICAL RESULTS</p>
                  <p className="text-2xl font-bold text-purple-400 font-orbitron">{extractedData?.summary?.total_analytical_results || 0}</p>
                  <p className="text-mono-600 text-xs mt-1">
                    {extractedData?.summary?.latest_sample_date
                      ? `Latest: ${new Date(extractedData.summary.latest_sample_date).toLocaleDateString()}`
                      : 'No data'}
                  </p>
                </div>
                <div className="bg-mono-900 rounded-lg p-4 border border-mono-800">
                  <p className="text-mono-500 text-xs tracking-wider mb-1">COMPOUNDS DETECTED</p>
                  <p className="text-2xl font-bold text-amber-400 font-orbitron">{extractedData?.summary?.parameters_detected?.length || 0}</p>
                  <p className="text-mono-600 text-xs mt-1 truncate" title={extractedData?.summary?.parameters_detected?.join(', ')}>
                    {extractedData?.summary?.parameters_detected?.slice(0, 3).join(', ') || 'None'}
                    {(extractedData?.summary?.parameters_detected?.length || 0) > 3 ? '...' : ''}
                  </p>
                </div>
              </div>

              {/* Loading State */}
              {extractedDataLoading && (
                <div className="bg-mono-900 border border-mono-800 rounded-lg p-8 text-center">
                  <ArrowPathIcon className="h-8 w-8 text-mono-500 animate-spin mx-auto mb-4" />
                  <p className="text-mono-400 text-sm">Loading well data from database...</p>
                </div>
              )}

              {/* Comprehensive Wells Table */}
              {!extractedDataLoading && wellsWithData.length > 0 && (
                <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">
                      SITE MONITORING WELLS
                    </h3>
                    <span className="text-mono-500 text-xs">
                      {wellsWithData.length} wells with coordinates
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-mono-800">
                          <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-3">WELL ID</th>
                          <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-3">TYPE</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">LATITUDE</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">LONGITUDE</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">DEPTH (ft)</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">DTW (ft)</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">WT ELEV (ft)</th>
                          <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-3">LATEST SAMPLE</th>
                          <th className="text-center text-mono-500 text-xs tracking-wider py-3 px-3">DATA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wellsWithData.map((well, idx) => {
                          // Find max concentration from latest analytical
                          const maxConc = well.latestAnalytical.reduce((max, a) => {
                            const conc = a.concentration_ug_l || 0;
                            return conc > max.conc ? { conc, compound: a.parameter, exceeds: a.exceeds_mcl } : max;
                          }, { conc: 0, compound: '', exceeds: false as boolean | null });

                          return (
                            <tr key={idx} className="border-b border-mono-800/50 hover:bg-mono-850">
                              <td className="py-3 px-3">
                                <span className="text-mono-100 text-sm font-mono font-medium">{well.well_name || well.well_id}</span>
                              </td>
                              <td className="py-3 px-3">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  well.well_type === 'recovery' ? 'bg-green-500/20 text-green-400' :
                                  well.well_type === 'mdpe' ? 'bg-purple-500/20 text-purple-400' :
                                  'bg-cyan-500/20 text-cyan-400'
                                }`}>
                                  {(well.well_type || 'MW').toUpperCase()}
                                </span>
                              </td>
                              <td className="text-right py-3 px-3">
                                <span className="text-mono-300 text-sm font-mono">
                                  {well.latitude?.toFixed(6) || '-'}
                                </span>
                              </td>
                              <td className="text-right py-3 px-3">
                                <span className="text-mono-300 text-sm font-mono">
                                  {well.longitude?.toFixed(6) || '-'}
                                </span>
                              </td>
                              <td className="text-right py-3 px-3">
                                <span className="text-mono-400 text-sm">
                                  {well.total_depth_ft?.toFixed(1) || '-'}
                                </span>
                              </td>
                              <td className="text-right py-3 px-3">
                                <span className={`text-sm ${well.latestGauging?.is_dry ? 'text-orange-400' : 'text-blue-400'}`}>
                                  {well.latestGauging?.depth_to_water_ft?.toFixed(2) || '-'}
                                  {well.latestGauging?.is_dry && ' (dry)'}
                                </span>
                              </td>
                              <td className="text-right py-3 px-3">
                                <span className="text-blue-400 text-sm">
                                  {well.latestGauging?.water_elevation_ft?.toFixed(2) || '-'}
                                </span>
                              </td>
                              <td className="py-3 px-3">
                                {maxConc.compound ? (
                                  <div className="text-xs">
                                    <span className={`font-medium ${maxConc.exceeds ? 'text-red-400' : 'text-mono-300'}`}>
                                      {(maxConc.conc / 1000).toFixed(3)} mg/L
                                    </span>
                                    <span className="text-mono-500 ml-1">
                                      ({maxConc.compound})
                                    </span>
                                    {maxConc.exceeds && (
                                      <span className="text-red-400 ml-1">⚠</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-mono-600 text-xs">No data</span>
                                )}
                              </td>
                              <td className="text-center py-3 px-3">
                                <div className="flex items-center justify-center gap-1">
                                  {well.gaugingCount > 0 && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/20 text-blue-400" title={`${well.gaugingCount} gauging records`}>
                                      G:{well.gaugingCount}
                                    </span>
                                  )}
                                  {well.analyticalCount > 0 && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-500/20 text-purple-400" title={`${well.analyticalCount} analytical results`}>
                                      A:{well.analyticalCount}
                                    </span>
                                  )}
                                  {well.gaugingCount === 0 && well.analyticalCount === 0 && (
                                    <span className="text-mono-600 text-xs">-</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Legend */}
                  <div className="mt-4 pt-4 border-t border-mono-800 flex items-center gap-6 text-xs text-mono-500">
                    <span><strong>DTW</strong> = Depth to Water</span>
                    <span><strong>WT ELEV</strong> = Water Table Elevation</span>
                    <span><span className="text-blue-400">G:#</span> = Gauging records</span>
                    <span><span className="text-purple-400">A:#</span> = Analytical results</span>
                    <span><span className="text-red-400">⚠</span> = Exceeds MCL</span>
                  </div>
                </div>
              )}

              {/* No wells message */}
              {!extractedDataLoading && wellsWithData.length === 0 && !site.existing_wells?.length && (
                <div className="bg-mono-900 border border-mono-800 rounded-lg p-8 text-center">
                  <CircleStackIcon className="h-12 w-12 text-mono-600 mx-auto mb-4" />
                  <h3 className="text-mono-300 font-medium mb-2">No Wells Found</h3>
                  <p className="text-mono-500 text-sm max-w-md mx-auto">
                    No monitoring wells have been added to this site yet. Upload documents through the Documents page to extract well data, or add wells manually.
                  </p>
                </div>
              )}

              {/* Well Gauging Data Table (from extracted gradient if available) */}
              {extractedGradientMap && (
                <GradientDataTable
                  gradientMap={extractedGradientMap}
                  siteName={site.site_name}
                />
              )}

              {/* Breach Analysis Section */}
              {(site.existing_wells?.length || 0) > 0 && (
                <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">
                      PLUME BREACH ANALYSIS
                    </h3>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        <span className="text-mono-400">Breached ({wellsBreachedByMonth.length})</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                        <span className="text-mono-400">At Risk ({wellsAtRisk.length})</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        <span className="text-mono-400">Safe ({wellsSafe.length})</span>
                      </span>
                    </div>
                  </div>

                  {/* Breach Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-mono-950 rounded-lg p-4 border border-red-500/30">
                      <p className="text-mono-500 text-xs tracking-wider mb-1">WELLS BREACHED BY {selectedMonth}mo</p>
                      <p className="text-2xl font-bold text-red-400 font-orbitron">{wellsBreachedByMonth.length}</p>
                    </div>
                    <div className="bg-mono-950 rounded-lg p-4 border border-orange-500/30">
                      <p className="text-mono-500 text-xs tracking-wider mb-1">WELLS AT FUTURE RISK</p>
                      <p className="text-2xl font-bold text-orange-400 font-orbitron">{wellsAtRisk.length}</p>
                    </div>
                    <div className="bg-mono-950 rounded-lg p-4 border border-mono-800">
                      <p className="text-mono-500 text-xs tracking-wider mb-1">NEW WELLS RECOMMENDED</p>
                      <p className="text-2xl font-bold text-mono-100 font-orbitron">{currentRecommendedWells?.wellsNeeded || 0}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost Summary */}
              <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron mb-4">
                  WELL COST SUMMARY
                </h3>
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <p className="text-mono-600 text-xs tracking-wider mb-1">ANNUAL MONITORING COST</p>
                    <p className="text-mono-100 text-xl font-bold font-orbitron">
                      {formatCurrency((extractedData?.wells?.length || site.existing_wells?.length || 0) * 2500)}
                    </p>
                    <p className="text-mono-600 text-xs">$2,500/well/year sampling</p>
                  </div>
                  <div>
                    <p className="text-mono-600 text-xs tracking-wider mb-1">NEW WELL INSTALLATION</p>
                    <p className="text-mono-100 text-xl font-bold font-orbitron">{formatCurrency(totalNewWellsCost)}</p>
                    <p className="text-mono-600 text-xs">{recommendedWellsPlan.reduce((sum, p) => sum + p.wellsNeeded, 0)} wells over 24mo</p>
                  </div>
                  <div>
                    <p className="text-mono-600 text-xs tracking-wider mb-1">BREACH REMEDIATION COST</p>
                    <p className="text-mono-100 text-xl font-bold font-orbitron">
                      {formatCurrency(wellsBreachedByMonth.length * 75000)}
                    </p>
                    <p className="text-mono-600 text-xs">$75K avg per breached well</p>
                  </div>
                </div>
              </div>

              {/* TWDB Wells Section */}
              <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">
                      TWDB WELLS
                    </h3>
                    <p className="text-mono-500 text-xs mt-1">
                      Texas Water Development Board wells within 1 mile of site
                    </p>
                  </div>
                  <span className="text-mono-500 text-xs">
                    {twdbWells.length} wells found
                  </span>
                </div>

                {/* Loading State */}
                {twdbLoading && (
                  <div className="flex items-center justify-center py-8">
                    <ArrowPathIcon className="h-6 w-6 text-mono-500 animate-spin mr-2" />
                    <span className="text-mono-400 text-sm">Loading TWDB wells...</span>
                  </div>
                )}

                {/* Error State */}
                {twdbError && !twdbLoading && (
                  <div className="text-center py-8">
                    <ExclamationTriangleIcon className="h-8 w-8 text-orange-400 mx-auto mb-2" />
                    <p className="text-mono-400 text-sm">{twdbError}</p>
                  </div>
                )}

                {/* TWDB Wells Table */}
                {!twdbLoading && !twdbError && twdbWells.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-mono-800">
                          <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-3">STATE WELL #</th>
                          <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-3">OWNER</th>
                          <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-3">TYPE</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">LATITUDE</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">LONGITUDE</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">DEPTH (ft)</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">ELEV (ft)</th>
                          <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-3">AQUIFER</th>
                          <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-3">DISTANCE</th>
                          <th className="text-center text-mono-500 text-xs tracking-wider py-3 px-3">DATA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {twdbWells.slice(0, 20).map((well, idx) => (
                          <tr key={idx} className="border-b border-mono-800/50 hover:bg-mono-850">
                            <td className="py-3 px-3">
                              <span className="text-mono-100 text-sm font-mono font-medium">{well.state_well_number}</span>
                            </td>
                            <td className="py-3 px-3">
                              <span className="text-mono-400 text-xs truncate max-w-[150px] block" title={well.owner_name || ''}>
                                {well.owner_name || '-'}
                              </span>
                            </td>
                            <td className="py-3 px-3">
                              <span className="text-mono-400 text-xs">{well.well_type || '-'}</span>
                            </td>
                            <td className="text-right py-3 px-3">
                              <span className="text-mono-300 text-sm font-mono">{well.latitude.toFixed(6)}</span>
                            </td>
                            <td className="text-right py-3 px-3">
                              <span className="text-mono-300 text-sm font-mono">{well.longitude.toFixed(6)}</span>
                            </td>
                            <td className="text-right py-3 px-3">
                              <span className="text-mono-400 text-sm">{well.well_depth_ft?.toFixed(0) || '-'}</span>
                            </td>
                            <td className="text-right py-3 px-3">
                              <span className="text-mono-400 text-sm">{well.elevation_ft || '-'}</span>
                            </td>
                            <td className="py-3 px-3">
                              <span className="text-mono-400 text-xs truncate max-w-[120px] block" title={well.aquifer_name || ''}>
                                {well.aquifer_name || '-'}
                              </span>
                            </td>
                            <td className="text-right py-3 px-3">
                              <span className="text-mono-300 text-sm">
                                {well.distance_miles ? `${well.distance_miles.toFixed(2)} mi` : '-'}
                              </span>
                            </td>
                            <td className="text-center py-3 px-3">
                              <div className="flex items-center justify-center gap-1">
                                {well.water_level_obs_type && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/20 text-blue-400" title="Water level observations available">
                                    WL
                                  </span>
                                )}
                                {well.water_quality_available === 'Y' && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-500/20 text-purple-400" title="Water quality data available">
                                    WQ
                                  </span>
                                )}
                                {!well.water_level_obs_type && well.water_quality_available !== 'Y' && (
                                  <span className="text-mono-600 text-xs">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {twdbWells.length > 20 && (
                      <p className="text-center text-mono-600 text-xs mt-3">
                        Showing 20 of {twdbWells.length} TWDB wells
                      </p>
                    )}
                  </div>
                )}

                {/* No wells found */}
                {!twdbLoading && !twdbError && twdbWells.length === 0 && (
                  <div className="text-center py-8">
                    <CircleStackIcon className="h-10 w-10 text-mono-600 mx-auto mb-3" />
                    <p className="text-mono-400 text-sm">No TWDB wells found within 1 mile of site</p>
                    <p className="text-mono-600 text-xs mt-1">
                      TWDB data is only available for Texas locations
                    </p>
                  </div>
                )}

                {/* Legend */}
                {twdbWells.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-mono-800 flex items-center gap-6 text-xs text-mono-500">
                    <span><span className="text-blue-400">WL</span> = Water Level data available</span>
                    <span><span className="text-purple-400">WQ</span> = Water Quality data available</span>
                    <span>Source: Texas Water Development Board</span>
                  </div>
                )}
              </div>
            </div>
          </FadeIn>
        )}

        {/* Tab 6: Well Predictions - ML Concentration Forecasts.
            FadeIn intentionally omitted — the IntersectionObserver inside
            `useScrollAnimation` doesn't always fire when this block toggles
            on after page load, leaving the content stuck at opacity 0. */}
        {shouldRender('predictions') && (
          <div className="space-y-6">
            {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-mono-900 rounded-lg p-4 border border-mono-800">
                  <p className="text-mono-500 text-xs tracking-wider mb-1">WELLS ASSESSED</p>
                  <p className="text-2xl font-bold text-cyan-400 font-orbitron">
                    {wellPredictions.length > 0
                      ? new Set(wellPredictions.map(p => p.well_id)).size
                      : 0}
                  </p>
                  <p className="text-mono-600 text-xs mt-1">with predictions</p>
                </div>
                <div className="bg-mono-900 rounded-lg p-4 border border-mono-800">
                  <p className="text-mono-500 text-xs tracking-wider mb-1">EXCEEDING MCL</p>
                  <p className="text-2xl font-bold text-red-400 font-orbitron">
                    {wellPredictions.filter(p => p.exceedance_probability > 0.5).length}
                  </p>
                  <p className="text-mono-600 text-xs mt-1">&gt;50% probability</p>
                </div>
                <div className="bg-mono-900 rounded-lg p-4 border border-mono-800">
                  <p className="text-mono-500 text-xs tracking-wider mb-1">IMPROVING</p>
                  <p className="text-2xl font-bold text-green-400 font-orbitron">
                    {wellPredictions.filter(p => p.trend === 'decreasing').length}
                  </p>
                  <p className="text-mono-600 text-xs mt-1">declining trend</p>
                </div>
                <div className="bg-mono-900 rounded-lg p-4 border border-mono-800">
                  <p className="text-mono-500 text-xs tracking-wider mb-1">ANALYTES</p>
                  <p className="text-2xl font-bold text-purple-400 font-orbitron">{predictionsAnalytes.length}</p>
                  <p className="text-mono-600 text-xs mt-1 truncate" title={predictionsAnalytes.join(', ')}>
                    {predictionsAnalytes.slice(0, 4).join(', ')}{predictionsAnalytes.length > 4 ? '...' : ''}
                  </p>
                </div>
              </div>

              {/* Loading State */}
              {predictionsLoading && (
                <div className="bg-mono-900 border border-mono-800 rounded-lg p-8 text-center">
                  <ArrowPathIcon className="h-8 w-8 text-mono-500 animate-spin mx-auto mb-4" />
                  <p className="text-mono-400 text-sm">Loading concentration predictions (running v3.2 inference if needed)...</p>
                </div>
              )}

              {/* Inference-status banner — shown whenever something went wrong
                  with the auto-trigger, regardless of how many rows came back. */}
              {!predictionsLoading && predictionsError && wellPredictions.length > 0 && (
                <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 text-[12px] text-amber-300">
                  <span className="font-semibold uppercase tracking-wider text-amber-400">Inference notice:</span>{' '}
                  {predictionsError}
                </div>
              )}

              {/* Generate Predictions Button - shown when no predictions */}
              {!predictionsLoading && wellPredictions.length === 0 && (
                <div className="bg-mono-900 border border-mono-800 rounded-lg p-8 text-center">
                  <SparklesIcon className="h-12 w-12 text-mono-600 mx-auto mb-4" />
                  <h3 className="text-mono-300 font-medium mb-2">No Predictions Available</h3>
                  <p className="text-mono-500 text-sm max-w-md mx-auto mb-6">
                    {predictionsError || 'Concentration predictions have not been generated for this site yet. Generate predictions to see forecasted concentration trends and exceedance probabilities.'}
                  </p>
                  <button
                    onClick={async () => {
                      if (!siteId) return;
                      setPredictionsLoading(true);
                      try {
                        await polygonApi.generatePredictions(siteId, 40);
                        // Refetch single + timeline together so the horizon table populates.
                        const [single, timeline] = await Promise.all([
                          polygonApi.getWellPredictions(siteId, 1, 200),
                          polygonApi.getTimelinePredictions(siteId).catch(() => null),
                        ]);
                        setWellPredictions(single.predictions || []);
                        setPredictionsAnalytes(single.analytes || []);
                        setTimelinePredictions(timeline);
                        setPredictionsError(null);
                      } catch (err) {
                        console.error('[POLYGON] Failed to generate predictions:', err);
                        setPredictionsError('Failed to generate predictions. Ensure the site has analytical data.');
                      } finally {
                        setPredictionsLoading(false);
                      }
                    }}
                    className="px-6 py-3 bg-purple-500/20 border border-purple-500/50 hover:bg-purple-500/30 text-purple-400 text-xs tracking-wider transition-colors flex items-center gap-2 mx-auto"
                  >
                    <SparklesIcon className="h-4 w-4" />
                    GENERATE PREDICTIONS
                  </button>
                </div>
              )}

              {/* Analyte filter — the only control left after dropping the
                  v3.1 Cards/Rows legacy views in favor of the Horizon table. */}
              {!predictionsLoading && wellPredictions.length > 0 && (
                <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-mono-500 text-xs tracking-wider">FILTER:</span>
                    <button
                      onClick={() => setSelectedAnalyteFilter('all')}
                      className={`px-3 py-1 rounded text-xs tracking-wider transition-colors ${
                        selectedAnalyteFilter === 'all'
                          ? 'bg-purple-500/30 text-purple-400 border border-purple-500/50'
                          : 'bg-mono-800 text-mono-400 hover:bg-mono-700'
                      }`}
                    >
                      ALL
                    </button>
                    {predictionsAnalytes.map(analyte => (
                      <button
                        key={analyte}
                        onClick={() => setSelectedAnalyteFilter(analyte)}
                        className={`px-3 py-1 rounded text-xs tracking-wider transition-colors ${
                          selectedAnalyteFilter === analyte
                            ? 'bg-purple-500/30 text-purple-400 border border-purple-500/50'
                            : 'bg-mono-800 text-mono-400 hover:bg-mono-700'
                        }`}
                      >
                        {analyte}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Well Predictions — Horizon Table (the only view now). */}
              {!predictionsLoading && wellPredictions.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">
                      WELL PREDICTIONS BY HORIZON
                    </h3>
                    <span className="text-mono-500 text-xs">
                      v3.2 ensemble · BTEX 3mo→20yr · TPH 3mo→2yr
                    </span>
                  </div>
                  <HorizonPredictionsTable
                    data={timelinePredictions}
                    loading={false}
                    analyteFilter={selectedAnalyteFilter}
                  />
                </div>
              )}


              {/* Model Info */}
              {!predictionsLoading && wellPredictions.length > 0 && (
                <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
                  <div className="flex items-center justify-between text-xs text-mono-500">
                    <div className="flex items-center gap-4">
                      <span>Model: {wellPredictions[0]?.model_type || 'XGBoost'}</span>
                      <span>Version: {wellPredictions[0]?.model_version || 'v2.3.1'}</span>
                    </div>
                    <span>
                      Predictions as of: {wellPredictions[0]?.prediction_date
                        ? new Date(wellPredictions[0].prediction_date).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              )}
          </div>
        )}

        {/* Tab 7: Delineation Intelligence — carbon copy of Phreatic Well Intelligence */}
        {shouldRender('delineation-intelligence') && (
          <FadeIn delay={200}>
            <DelineationIntelligencePanel
              siteId={siteId!}
              centerLat={site.latitude}
              centerLon={site.longitude}
              existingWells={(extractedData?.wells || []).map(w => ({
                well_id: w.well_id,
                well_name: w.well_name,
                latitude: w.latitude,
                longitude: w.longitude,
                total_depth_ft: w.total_depth_ft,
              }))}
              avgExistingWellDepthFt={avgExistingWellDepthFt ?? undefined}
              propertyBounds={null}
              regLibrary={regLibrary}
            />
          </FadeIn>
        )}
      </div>

      {/* Bottom action row removed per UX request (DELETE SITE moves to a
          settings/admin pathway later if needed). */}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-mono-900 border border-mono-700 rounded-lg p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-mono-100 font-semibold">Delete Site?</h3>
                <p className="text-mono-500 text-sm">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-mono-400 text-sm mb-6">
              Are you sure you want to delete <span className="text-mono-200 font-medium">{site?.site_name || siteId}</span>?
              All analysis data will be permanently removed.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 bg-mono-800 hover:bg-mono-700 text-mono-300 text-xs tracking-wider transition-colors disabled:opacity-50"
              >
                CANCEL
              </button>
              <button
                onClick={handleDeleteSite}
                disabled={deleting}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-xs tracking-wider transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    DELETING...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4" />
                    DELETE SITE
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Multi-Phase Coupling Physics — explanatory diagram. Static content; no props.
// Lives on the SITE DATA tab so it accompanies hydrogeology context.
function CouplingPhysicsCard() {
  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
      <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider mb-3">
        Multi-Phase Coupling Physics
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <div className="w-4 h-4 rounded-full bg-amber-500" />
          </div>
          <div>
            <div className="font-medium text-amber-400 mb-1">PSH/LNAPL (Source)</div>
            <p className="text-mono-500">
              Non-aqueous phase liquid at the water table. Depletes through dissolution into groundwater,
              volatilization into soil gas, and biodegradation. Shrinks over time as mass is transferred
              to other phases.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <div className="w-4 h-4 rounded-full bg-cyan-500" />
          </div>
          <div>
            <div className="font-medium text-cyan-400 mb-1">Dissolved Plume</div>
            <p className="text-mono-500">
              Contaminants dissolved in groundwater. Fed by PSH dissolution (Raoult's Law).
              Migrates downgradient following groundwater flow. Grows as PSH dissolves,
              then may shrink as source depletes.
            </p>
          </div>
        </div>
      </div>

      {/* Coupling arrows visualization */}
      <div className="mt-4 pt-4 border-t border-mono-800">
        <div className="flex items-center justify-center gap-8">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-amber-500/30 border-2 border-amber-500 flex items-center justify-center mx-auto mb-2">
              <span className="text-amber-400 font-bold text-xs">PSH</span>
            </div>
          </div>

          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1">
              <div className="w-16 h-0.5 bg-gradient-to-r from-amber-500 to-cyan-500" />
              <span className="text-[10px] text-mono-500">dissolution</span>
            </div>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-cyan-500/30 border-2 border-cyan-500 flex items-center justify-center mx-auto mb-1">
              <span className="text-cyan-400 font-bold text-[10px]">DIS</span>
            </div>
            <span className="text-[10px] text-mono-500">Dissolved</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sidebar tab item — matches Phreatic SidebarTab. Click dot = toggle in dashboard;
// click label = enter solo mode for that tab.
function PolygonSidebarTab({ label, isSolo, isChecked, viewMode, onToggle, onSolo }: {
  label: string;
  isSolo: boolean;
  isChecked: boolean;
  viewMode: string;
  onToggle: () => void;
  onSolo: () => void;
}) {
  return (
    <div
      className={`flex items-center gap-2 py-1.5 px-2 rounded text-[11px] cursor-pointer group transition-colors ${
        isSolo
          ? 'bg-cyan-500/10 text-cyan-300'
          : isChecked && viewMode === 'dashboard'
          ? 'bg-mono-800/50 text-mono-200'
          : 'text-mono-500 hover:text-mono-300 hover:bg-mono-900'
      }`}
    >
      <button
        onClick={e => { e.stopPropagation(); onToggle(); }}
        className={`w-3 h-3 rounded-full flex-shrink-0 border transition-colors ${
          isChecked
            ? 'bg-cyan-500 border-cyan-400'
            : 'bg-mono-700 border-mono-600 group-hover:border-mono-500'
        }`}
      />
      <span onClick={onSolo} className="flex-1 truncate leading-tight">{label}</span>
    </div>
  );
}
